import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Typography } from '@blade/ui-kit'
import {
  reatomWalletSummary,
  summaryModelAtom,
} from '@root/entities/summary/atoms/reatom-wallets-summary'
import { Skeleton } from '@root/shared/ui-kit'

import { SummaryChart } from '../common/summary-chart'
import { SummaryChartChange } from '../common/summary-chart-change'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = ClassNameProps & {
  wallet: WalletModel | undefined
}

export const SummaryChartMiniature: React.FC<Props> = ({
  className,
  wallet,
}) => {
  const { t } = useTranslation()
  const [model] = useAtom((ctx) =>
    wallet ? reatomWalletSummary(wallet.id) : ctx.spy(summaryModelAtom),
  )
  const [chartData] = useAtom(model.chartData)
  const [priceChangeState] = useAtom(model.priceChangeState)

  return AsyncStateN.matchWaiting(priceChangeState, {
    Error: () => (
      <EmptyChart className={className}>
        <EmptyChartMessage>{t('common.loading-error')}</EmptyChartMessage>
        <EmptyChartFooter />
      </EmptyChart>
    ),
    Waiting: () => (
      <SkeletonContainer className={className}>
        <Skeleton height="80%" />
      </SkeletonContainer>
    ),
    Success: () => {
      if (chartData === undefined) {
        return (
          <EmptyChart className={className}>
            <EmptyChartMessage>{t('common.no_data')}</EmptyChartMessage>
            <EmptyChartFooter />
          </EmptyChart>
        )
      }

      return (
        <Root className={className}>
          <ChartArea>
            <SummaryChart
              data={chartData}
              changePercentage={priceChangeState.value?.percentage ?? 0}
            />
            <TimeFrame variant="description-semi-bold">
              {t('common.30d')}
            </TimeFrame>
          </ChartArea>
          <Change
            change={priceChangeState.value?.absolute ?? BigNumber(0)}
            changePercentage={priceChangeState.value?.percentage ?? 0}
          />
        </Root>
      )
    },
  })
}

const Root = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
`

const SkeletonContainer = styled.div`
  display: grid;
`

const ChartArea = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 4px;
`

const TimeFrame = styled(Typography)`
  color: var(--textA-9);
`

const Change = styled(SummaryChartChange)`
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
  text-align: center;
`

const EmptyChart = styled.div`
  color: var(--text-8);

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  display: grid;
  grid-template-rows: 1fr auto;
  align-content: center;
`

const EmptyChartMessage = styled.span`
  display: grid;
  align-content: center;
  justify-content: center;
  text-align: center;
`

const EmptyChartFooter = styled.div`
  height: 2px;
  background: repeating-linear-gradient(
    to right,
    var(--backgroundA-5) 0 2px,
    transparent 2px 4px
  );
`
